import React, { useEffect } from 'react';
import axios from 'axios';
import { AgGridReact } from 'ag-grid-react';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

import {
  API_URL_LANES_PRICING,
  API_URL_TENDER_DETAILS,
  API_URL_TENDERS_FINAL,
  API_URL_TENDER_DEPARTMENT_STATUS,
  API_BASE_URL,
  tenderColumnsDefinitionOverview,
} from '../configuration/Configuration';
import { headerColumns, isPosted, exportDepartmentStatusToExcel } from '../configuration/Utils';
import { useLocation, useNavigate } from 'react-router-dom';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import TableControls from '../integrations/core/table/TableControls';
import {
  applySavedColumnLayout,
  getColumnLayoutScope,
  saveGridColumnLayout,
} from '../integrations/core/table/columnLayoutPersistence';
import TenderDocuments from './TenderDocuments';
import { useAuth } from '../context/AuthContext';

const removeEditingFromColumns = (columns) =>
  columns.map((col) => {
    const { cellEditor, cellEditorParams, ...rest } = col;
    return { ...rest, editable: false };
  });

const TenderOverview = () => {
  const { key } = useLocation();
  const navigate = useNavigate();
  const { user, userType } = useAuth();

  useEffect(() => {
    resetState();
    getTenderDataOverview();
  }, [key]);
  React.useEffect(() => {
    window.addEventListener('beforeunload', handleUnload);

    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  // eslint-disable-next-line
  let [tendersSelectionSate, setTenderSelectState] = React.useState(false);
  let [selectedTenderId, setSelectedTenderId] = React.useState<number | null>(null);
  let [selectedTenderStatus, setSelectedTenderStatus] = React.useState('');
  const overviewLayoutScope = getColumnLayoutScope();
  const detailLayoutScope = getColumnLayoutScope(selectedTenderId);

  // retrieve tender overview
  let [tenders, setOverview] = React.useState([]);
  // eslint-disable-next-line
  React.useEffect(() => {
    const opt = [];

    (async () => {
      const { data } = await axios.get(API_URL_TENDERS_FINAL);
      data.forEach((value) => {
        opt.push(value);
      });
    })();
    setOverview(opt);
  }, []);

  let fullScreenHandle = useFullScreenHandle();
  const [isFullScreen, setIsFullScreen] = React.useState(false);
  const reportChange = React.useCallback(
    (state: boolean, handle: ReturnType<typeof useFullScreenHandle>) => {
      const isActuallyFullscreen =
        state &&
        !!handle.node.current &&
        document.fullscreenElement === handle.node.current;
      setIsFullScreen(isActuallyFullscreen);
    },
    [fullScreenHandle]
  );
  const gridRef = React.useRef();
  const persistColumnLayout = React.useCallback((event, layoutScope) => {
    saveGridColumnLayout(event.columnApi, layoutScope);
  }, []);
  const persistColumnLayoutOnResize = React.useCallback((event, layoutScope) => {
    if (!event.finished) {
      return;
    }

    saveGridColumnLayout(event.columnApi, layoutScope);
  }, []);

  const onTenderSelected = (params) => {
    getTenderData(params.data.id);
    setTenderSelectState(true);
    setSelectedTenderId(params.data.id);
    setSelectedTenderStatus(params.data.tender_status);

    // Track tender view
    const trackTenderView = async () => {
      try {
        const username = localStorage.getItem('user');
        await axios.post(`${API_BASE_URL}/api/tender/track-view/`, {
          tender_id: params.data.id,
          username: username,
        });
      } catch (error) {
        console.error('Error tracking tender view:', error);
      }
    };
    trackTenderView();
  };

  const [tenderData, setTenderData] = React.useState({ rows: [] });
  const [gridAdmin, setGridAdmin] = React.useState([]);
  const [fillingNotes, setFillingNotes] = React.useState('');

  // Department status popup
  const [deptStatusOpen, setDeptStatusOpen] = React.useState(false);
  const [deptStatusRows, setDeptStatusRows] = React.useState([]);
  const [deptStatusLoading, setDeptStatusLoading] = React.useState(false);
  const [deptStatusError, setDeptStatusError] = React.useState('');

  const handleOpenDeptStatus = async () => {
    if (!selectedTenderId) return;
    setDeptStatusOpen(true);
    setDeptStatusLoading(true);
    setDeptStatusError('');
    try {
      const res = await axios.get(API_URL_TENDER_DEPARTMENT_STATUS(selectedTenderId), { withCredentials: true });
      setDeptStatusRows(res.data || []);
    } catch (err: any) {
      setDeptStatusError(err?.response?.data?.error || err?.message || 'Could not load department status.');
    } finally {
      setDeptStatusLoading(false);
    }
  };

  const getTenderData = (tender) => {
    (async () => {
      const url_api = API_URL_LANES_PRICING + tender + '/admin';
      const [{ data }, { data: tenderDetailsResponse }] = await Promise.all([
        axios.get(url_api),
        axios.get(API_URL_TENDER_DETAILS(tender)),
      ]);
      const tenderDetails = Array.isArray(tenderDetailsResponse)
        ? tenderDetailsResponse[0]
        : tenderDetailsResponse;
      setTenderData(data);
      setFillingNotes(tenderDetails?.filling_notes || '');
      let laneColumnDefinitionAdmin = await headerColumns(tender, true, false, userType?.isViewer);
      if (userType?.isViewer) {
        laneColumnDefinitionAdmin = removeEditingFromColumns(laneColumnDefinitionAdmin);
      }
      setGridAdmin(laneColumnDefinitionAdmin);
    })();
  };

  const getTenderDataOverview = () => {
    (async () => {
      const opt = [];
      const { data } = await axios.get(API_URL_TENDERS_FINAL);
      data.forEach((value: any) => {
        opt.push(value);
      });
      setOverview(opt);
    })();
  };

  const resetState = () => {
    setTenderSelectState(false);
    setSelectedTenderId(null);
    setSelectedTenderStatus('');
    setFillingNotes('');
    setDeptStatusOpen(false);
    setDeptStatusRows([]);
    setDeptStatusError('');
  };

  // reset states before unload
  const handleUnload = () => {
    setTenderSelectState(false);
    setOverview([]);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Typography variant="h6" align="left">
        Get an overview over tenders that reached their final state.
      </Typography>

      <FullScreen handle={fullScreenHandle} onChange={reportChange}>
        <div style={isFullScreen ? { display: 'flex', flexDirection: 'column', height: '100%' } : {}}>
        <TableControls
          table={gridRef}
          fullScreenHandle={fullScreenHandle}
          tenders={selectedTenderId}
          layoutScope={selectedTenderId ? detailLayoutScope : overviewLayoutScope}
        />
        {!tendersSelectionSate && (
          <div
            style={isFullScreen
              ? { flex: 1, minHeight: 0 }
              : { height: 540, width: '100%' }
            }
            className="ag-theme-alpine"
          >
            <AgGridReact
              ref={gridRef} // Ref for accessing Grid's API
              rowData={tenders} // Row Data for Rows
              columnDefs={applySavedColumnLayout(
                tenderColumnsDefinitionOverview,
                overviewLayoutScope
              )} // Column Defs for Columns
              onColumnMoved={(event) =>
                persistColumnLayout(event, overviewLayoutScope)
              }
              onColumnPinned={(event) =>
                persistColumnLayout(event, overviewLayoutScope)
              }
              onColumnVisible={(event) =>
                persistColumnLayout(event, overviewLayoutScope)
              }
              onColumnResized={(event) =>
                persistColumnLayoutOnResize(event, overviewLayoutScope)
              }
              onRowDoubleClicked={onTenderSelected}
              pagination={true}
              paginationAutoPageSize={true}
            />
          </div>
        )}
        {tendersSelectionSate && (
          <>
            {userType?.isAdmin && (
              // eslint-disable-next-line no-constant-binary-expression
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-start' }}>
                {selectedTenderStatus === 'Closed' && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => navigate(`/AdvanceTender?tender_id=${selectedTenderId}`)}
                    sx={{ mr: 1 }}
                  >
                    Reopen Tender
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleOpenDeptStatus}
                >
                  Show Tender Info
                </Button>
              </Box>
            )}
            {fillingNotes && (
              <Typography
                align="left"
                fontWeight={500}
                sx={{
                  backgroundColor: '#e3f2fd',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  marginTop: '8px',
                  marginBottom: '16px',
                  border: '1px solid #2196f3',
                  whiteSpace: 'pre-wrap',
                }}
              >
                <strong>Filling Instructions:</strong> {fillingNotes}
              </Typography>
            )}
            <div
              style={isFullScreen
                ? { flex: 1, minHeight: 0 }
                : { height: 540, width: '100%' }
              }
              className="ag-theme-alpine"
            >
              <AgGridReact
                ref={gridRef} // Ref for accessing Grid's API
                rowData={tenderData.rows} // Row Data for Rows
                columnDefs={gridAdmin} // Column Defs for Columns
                suppressClickEdit={userType?.isViewer || undefined}
                onColumnMoved={(event) =>
                  persistColumnLayout(event, detailLayoutScope)
                }
                onColumnPinned={(event) =>
                  persistColumnLayout(event, detailLayoutScope)
                }
                onColumnVisible={(event) =>
                  persistColumnLayout(event, detailLayoutScope)
                }
                onColumnResized={(event) =>
                  persistColumnLayoutOnResize(event, detailLayoutScope)
                }
                pagination={true}
                paginationAutoPageSize={true}
              />
            </div>
          </>
        )}
        
        {/* Document Management Section */}
        {tendersSelectionSate && selectedTenderId && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <TenderDocuments tenderId={selectedTenderId} username={user} />
          </Box>
        )}
        </div>
      </FullScreen>

      <Button
        disabled={!tendersSelectionSate}
        variant="outlined"
        size="large"
        onClick={resetState}
        color="inherit"
        style={{ alignSelf: 'end' }}
      >
        Back
      </Button>

      {/* Department Status Popup */}
      <Dialog open={deptStatusOpen} onClose={() => setDeptStatusOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Department Status</DialogTitle>
        <DialogContent>
          {deptStatusLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {deptStatusError && (
            <Alert severity="error" sx={{ mb: 2 }}>{deptStatusError}</Alert>
          )}
          {!deptStatusLoading && !deptStatusError && deptStatusRows.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No departments have been assigned to this tender yet.
            </Typography>
          )}
          {!deptStatusLoading && deptStatusRows.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Department</strong></TableCell>
                    <TableCell align="center"><strong>Viewed</strong></TableCell>
                    <TableCell align="center"><strong>Declined</strong></TableCell>
                    <TableCell><strong>Decline Reason</strong></TableCell>
                    <TableCell align="center"><strong>Document Uploaded</strong></TableCell>
                    <TableCell align="center"><strong>Prices Submitted</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deptStatusRows.map((row: any) => (
                    <TableRow key={row.department}>
                      <TableCell>{row.department}</TableCell>
                      <TableCell align="center">
                        {row.viewed
                          ? <CheckCircleOutlineIcon color="success" fontSize="small" />
                          : <RemoveCircleOutlineIcon color="disabled" fontSize="small" />}
                      </TableCell>
                      <TableCell align="center">
                        {row.declined
                          ? <CheckCircleOutlineIcon color="error" fontSize="small" />
                          : <RemoveCircleOutlineIcon color="disabled" fontSize="small" />}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 250, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {row.declined ? row.decline_reason : '—'}
                      </TableCell>
                      <TableCell align="center">
                        {row.document_uploaded
                          ? <CheckCircleOutlineIcon color="success" fontSize="small" />
                          : <RemoveCircleOutlineIcon color="disabled" fontSize="small" />}
                      </TableCell>
                      <TableCell align="center">
                        {row.prices_submitted
                          ? <CheckCircleOutlineIcon color="success" fontSize="small" />
                          : <RemoveCircleOutlineIcon color="disabled" fontSize="small" />}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          {deptStatusRows.length > 0 && (
            <Button onClick={() => exportDepartmentStatusToExcel(selectedTenderId!, deptStatusRows)}>
              Export
            </Button>
          )}
          <Button onClick={() => setDeptStatusOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TenderOverview;
